import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { agentDashboardsApi } from "../api/agentDashboards";
import { metricAlertsApi } from "../api/metricAlerts";
import { queryKeys } from "../lib/queryKeys";
import { DashboardWidgetCard } from "../components/charts/DashboardWidgetCard";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";
import { Button } from "@/components/ui/button";
import { cn } from "../lib/utils";
import { timeAgo } from "../lib/timeAgo";
import { BarChart3, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";

// Severity → chip styling for the active-issues summary (semantic, dark-mode safe).
const ALERT_SEVERITY: Record<string, string> = {
  critical:
    "bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800/40",
  warning:
    "bg-amber-50 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800/40",
  info:
    "bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800/40",
};

const SWIPE_THRESHOLD_PX = 50;

export function SimpleDashboards() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Dashboards" }]);
  }, [setBreadcrumbs]);

  const { data: dashboards, isLoading } = useQuery({
    queryKey: queryKeys.agentDashboards.list(selectedPracticeId!),
    queryFn: () => agentDashboardsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  // Active (unacknowledged) metric breaches — the "issue + summary" surfaced
  // on the dashboard so it's not just raw numbers. Refetches alongside the feed.
  const { data: activeAlerts } = useQuery({
    queryKey: ["metric-alerts", selectedPracticeId, "unacknowledged"],
    queryFn: () => metricAlertsApi.list(selectedPracticeId!, { acknowledged: false, limit: 20 }),
    enabled: !!selectedPracticeId,
    refetchInterval: 60_000,
  });

  // Clamp the active index if the dashboard list shrinks.
  const count = dashboards?.length ?? 0;
  const safeIndex = count > 0 ? Math.min(index, count - 1) : 0;
  const active = dashboards?.[safeIndex] ?? null;

  const { data: detail, isLoading: isDetailLoading } = useQuery({
    queryKey: queryKeys.agentDashboards.detail(active?.id ?? "none"),
    queryFn: () => agentDashboardsApi.get(active!.id),
    enabled: !!active,
  });

  function go(delta: number) {
    if (count === 0) return;
    setIndex((prev) => {
      const next = (prev + delta + count) % count;
      return next;
    });
  }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
    const dx = endX - touchStartX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  }

  if (!selectedPracticeId) {
    return <EmptyState icon={BarChart3} message="Select a practice to view dashboards." />;
  }

  if (isLoading && !dashboards) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <PageSkeleton variant="dashboard" />
      </div>
    );
  }

  if (count === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        message="No dashboards yet. They're created automatically as metrics get approved."
      />
    );
  }

  const widgets = detail?.widgets ?? [];
  const alerts = activeAlerts ?? [];

  return (
    <div
      className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Active issues — what's wrong right now, with a one-line summary, so the
          dashboard surfaces breaches (not just raw numbers). Mirrors the alerts
          fanned out to email + Google Chat + the Inbox. */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold">
              {alerts.length} active {alerts.length === 1 ? "issue" : "issues"}
            </span>
            <span className="text-xs text-muted-foreground">needs attention</span>
          </div>
          <ul className="divide-y divide-border">
            {alerts.map((a) => (
              <li key={a.id} className="flex items-start gap-3 px-4 py-2.5">
                <span
                  className={cn(
                    "mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                    ALERT_SEVERITY[a.severity] ?? ALERT_SEVERITY.info,
                  )}
                >
                  {a.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium">{a.thresholdLabel}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {timeAgo(a.createdAt)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{a.message}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {alerts.length === 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          All monitored metrics are within range.
        </div>
      )}

      {/* Header + carousel controls */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight">
            {active?.title ?? "Dashboards"}
          </h1>
          {active?.description && (
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {active.description}
            </p>
          )}
        </div>
        {count > 1 && (
          <div className="flex shrink-0 items-center gap-1.5">
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => go(-1)} aria-label="Previous dashboard">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-1 text-xs tabular-nums text-muted-foreground">
              {safeIndex + 1} / {count}
            </span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => go(1)} aria-label="Next dashboard">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Widgets for the active dashboard */}
      {isDetailLoading && widgets.length === 0 ? (
        <PageSkeleton variant="dashboard" />
      ) : widgets.length === 0 ? (
        <EmptyState icon={BarChart3} message="This dashboard has no widgets yet." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {widgets.map((w) => (
            <DashboardWidgetCard key={w.id} widget={w} />
          ))}
        </div>
      )}

      {/* Dot indicators */}
      {count > 1 && (
        <div className="flex items-center justify-center gap-2 pt-1">
          {dashboards!.map((d, i) => (
            <button
              key={d.id}
              type="button"
              aria-label={`Go to ${d.title}`}
              onClick={() => setIndex(i)}
              className={cn(
                "h-2 rounded-full transition-all",
                i === safeIndex ? "w-6 bg-foreground/70" : "w-2 bg-foreground/20 hover:bg-foreground/40",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
